import { KLineData } from "klinecharts";
import { klineLineStyle, lineStyleToKLine } from "./lineStyle";
import { readStyle } from "./indicatorUtils";

function smmaStep(prev: number, value: number, period: number): number {
  return (prev * (period - 1) + value) / period;
}

export const createAlligator = (settings: any) => {
  const values = settings?.values ?? {};
  const styles = settings?.styles ?? settings?.styleSettings ?? {};
  const jawPeriod = (values["Jaw Period"] as number) ?? 13;
  const jawShift = (values["Jaw Shift"] as number) ?? 8;
  const teethPeriod = (values["Teeth Period"] as number) ?? 8;
  const teethShift = (values["Teeth Shift"] as number) ?? 5;
  const lipsPeriod = (values["Lips Period"] as number) ?? 5;
  const lipsShift = (values["Lips Shift"] as number) ?? 3;

  const jawS = readStyle(styles, "jaw", "#2196F3");
  const teethS = readStyle(styles, "teeth", "#EF5350");
  const lipsS = readStyle(styles, "lips", "#26A69A");

  return {
    name: "alligator",
    shortName: "Alligator",
    calcParams: [
      jawPeriod,
      jawShift,
      teethPeriod,
      teethShift,
      lipsPeriod,
      lipsShift,
    ],
    series: "price" as const,
    figures: [
      { key: "jaw", title: "Jaw: ", type: "line" },
      { key: "teeth", title: "Teeth: ", type: "line" },
      { key: "lips", title: "Lips: ", type: "line" },
    ],
    styles: {
      lines: [
        klineLineStyle(jawS.color, 2, lineStyleToKLine(jawS.style)),
        klineLineStyle(teethS.color, 2, lineStyleToKLine(teethS.style)),
        klineLineStyle(lipsS.color, 2, lineStyleToKLine(lipsS.style)),
      ],
    },
    regenerateFigures: () => [
      { key: "jaw", title: "Jaw: ", type: "line" },
      { key: "teeth", title: "Teeth: ", type: "line" },
      { key: "lips", title: "Lips: ", type: "line" },
    ],
    calc: (dataList: KLineData[], indicator: { calcParams?: unknown[] }) => {
      const [jP, jS, tP, tS, lP, lS] = (indicator.calcParams ?? [
        jawPeriod,
        jawShift,
        teethPeriod,
        teethShift,
        lipsPeriod,
        lipsShift,
      ]) as [number, number, number, number, number, number];
      const n = dataList.length;
      const results: Array<{
        jaw?: number;
        teeth?: number;
        lips?: number;
      }> = new Array(n);
      for (let i = 0; i < n; i++) results[i] = {};

      let jawSmma = 0;
      let teethSmma = 0;
      let lipsSmma = 0;
      let jawSeed = 0;
      let teethSeed = 0;
      let lipsSeed = 0;

      for (let i = 0; i < n; i++) {
        const median = (dataList[i]!.high + dataList[i]!.low) / 2;

        if (i < jP) {
          jawSeed += median;
          if (i === jP - 1) jawSmma = jawSeed / jP;
        } else jawSmma = smmaStep(jawSmma, median, jP);

        if (i < tP) {
          teethSeed += median;
          if (i === tP - 1) teethSmma = teethSeed / tP;
        } else teethSmma = smmaStep(teethSmma, median, tP);

        if (i < lP) {
          lipsSeed += median;
          if (i === lP - 1) lipsSmma = lipsSeed / lP;
        } else lipsSmma = smmaStep(lipsSmma, median, lP);

        if (i >= jP - 1) {
          const t = i + jS;
          if (t < n) results[t]!.jaw = jawSmma;
        }
        if (i >= tP - 1) {
          const t = i + tS;
          if (t < n) results[t]!.teeth = teethSmma;
        }
        if (i >= lP - 1) {
          const t = i + lS;
          if (t < n) results[t]!.lips = lipsSmma;
        }
      }

      return results;
    },
  };
};
